import { useEffect, useState } from 'react';
import api from '../../services/api';

// Elite by invite (her call 10/31): name and cell, review the exact text, then send. The account
// with that verified phone becomes Elite once it passes Get verified (or right away if it has).
const btn = { borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const primary = { ...btn, padding: '9px 16px', border: 'none', background: 'linear-gradient(135deg,#0A3FD6,#7229F5)', color: '#fff' };
const ghost = { ...btn, background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)' };
const input = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 12px', color: '#fff', fontSize: 14 };
const box = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 18 };
const STATUS = { draft: 'Not sent', sent: 'Sent, waiting for Get verified', used: 'Elite', cancelled: 'Cancelled', expired: 'Expired' };

const fmt = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; }
};
const errMsg = (e, fallback) => e?.response?.data?.message || fallback;

export default function EliteInvites({ onChanged }) {
  const [invites, setInvites] = useState([]);
  const [link, setLink] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [draft, setDraft] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/elite/admin/invites');
      setInvites(Array.isArray(data?.invites) ? data.invites : []);
      setLink(data?.link || '');
    } catch (e) {
      setError(errMsg(e, 'Could not load invites. Refresh to try again.'));
    }
  };
  useEffect(() => { load(); }, []);

  const preview = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setNote('');
    try {
      const { data } = await api.post('/elite/admin/invites', { name, phone });
      setDraft(data);
      setMessage(data.message);
      load();
    } catch (err) {
      setError(errMsg(err, 'Could not make the invite. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!message.trim()) { setError('The text is empty.'); return; }
    if (!window.confirm(`Text this invite to ${draft.name} at ${draft.phone}?`)) return;
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/elite/admin/invites/${draft.id}/send`, { message });
      setNote(data.granted_now
        ? `Sent. ${draft.name} was already verified, so they're Elite now.`
        : `Sent. ${draft.name} becomes Elite once they pass Get verified with this number, within 30 days.`);
      setDraft(null); setName(''); setPhone(''); setMessage('');
      load(); onChanged?.();
    } catch (err) {
      setError(errMsg(err, 'Could not send. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (inv) => {
    if (inv.status === 'sent' && !window.confirm(`Cancel ${inv.name}'s invite? The text already went out, but it won't make them Elite anymore.`)) return;
    try {
      await api.post(`/elite/admin/invites/${inv.id}/cancel`);
      if (draft?.id === inv.id) { setDraft(null); setMessage(''); }
      load();
    } catch (err) {
      setError(errMsg(err, 'Could not cancel. Try again.'));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={box}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 4 }}>Invite someone to Elite</div>
        <div style={{ color: 'rgba(255,255,255,.45)', fontSize: 13, marginBottom: 14 }}>
          They become Elite once they sign up with this cell and pass Get verified. If they already have, it's immediate. Open for 30 days.
        </div>
        {!draft ? (
          <form onSubmit={preview} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input style={{ ...input, flex: '1 1 180px' }} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
            <input style={{ ...input, flex: '1 1 180px' }} placeholder="Cell, like 305 555 0101" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
            <button type="submit" disabled={busy || !name.trim() || !phone.trim()} style={primary}>{busy ? '…' : 'Preview the text'}</button>
          </form>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ color: 'rgba(255,255,255,.7)', fontSize: 13 }}>To {draft.name} at {draft.phone}. Edit the words if you like; the link is added on its own line.</div>
            <textarea style={{ ...input, minHeight: 90, fontFamily: 'inherit' }} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={320} />
            <div style={{ color: 'rgba(255,255,255,.45)', fontSize: 12 }}>{link}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" disabled={busy} onClick={send} style={primary}>{busy ? '…' : 'Send text'}</button>
              <button type="button" disabled={busy} onClick={() => cancel(draft)} style={ghost}>Discard</button>
            </div>
          </div>
        )}
        {error && <div style={{ color: '#f87171', fontSize: 13, marginTop: 10 }}>{error}</div>}
        {note && <div style={{ color: '#7ee2a8', fontSize: 13, marginTop: 10 }}>{note}</div>}
      </div>

      {invites.filter((i) => i.status !== 'draft' || i.id !== draft?.id).map((inv) => (
        <div key={inv.id} style={{ ...box, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{inv.name} · {inv.phone}</div>
            <div style={{ color: 'rgba(255,255,255,.6)', fontSize: 13, marginTop: 4 }}>{STATUS[inv.status] || inv.status}</div>
            <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 11, marginTop: 4 }}>
              {inv.used_at ? `Elite ${fmt(inv.used_at)}` : inv.sent_at ? `Sent ${fmt(inv.sent_at)}, open until ${fmt(inv.expires_at)}` : `Made ${fmt(inv.created_at)}`}
            </div>
          </div>
          {inv.status === 'draft' && <button type="button" onClick={() => { setDraft(inv); setMessage(inv.message); }} style={ghost}>Review and send</button>}
          {['draft', 'sent'].includes(inv.status) && <button type="button" onClick={() => cancel(inv)} style={{ ...ghost, color: '#f87171', borderColor: 'rgba(248,113,113,.4)' }}>Cancel</button>}
        </div>
      ))}
    </div>
  );
}
